import React from 'react';
import { Layers, Ruler, Droplet, Clock3, Boxes, Weight, Package, Zap, HelpCircle } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { Unit } from '@/modelos/Unidad';
import type { ProductFormData } from '@/modelos/productos/Producto';
import Tooltip from '@/contasis/components/Tooltip/Tooltip';
import { Select, Chip } from '@/contasis';

type UnitFamily = Unit['category'];

const UNIT_FAMILY_META: Record<UnitFamily, { label: string; Icon: LucideIcon }> = {
  SERVICIOS: { label: 'Servicios', Icon: Layers },
  TIME: { label: 'Tiempos', Icon: Clock3 },
  WEIGHT: { label: 'Pesos', Icon: Weight },
  VOLUME: { label: 'Volúmenes', Icon: Droplet },
  LENGTH: { label: 'Longitudes', Icon: Ruler },
  AREA: { label: 'Áreas', Icon: Ruler },
  ENERGY: { label: 'Energías', Icon: Zap },
  QUANTITY: { label: 'Cantidades', Icon: Boxes },
  PACKAGING: { label: 'Empaques', Icon: Package },
  OTHER: { label: 'Otros', Icon: HelpCircle },
};

interface ProductUnitsSectionProps {
  formData: ProductFormData;
  selectedUnitFamily: UnitFamily;
  unitFamilies: UnitFamily[];
  baseUnitOptions: Unit[];
  isUsingFallbackUnits: boolean;
  handleUnitFamilyChange: (nextFamily: UnitFamily) => void;
  handleBaseUnitChange: (nextUnit: ProductFormData['unidad']) => void;
}

type ProductUnitFamilyFieldProps = Pick<
  ProductUnitsSectionProps,
  'isUsingFallbackUnits' | 'handleUnitFamilyChange' | 'selectedUnitFamily' | 'unitFamilies'
> & {
  /** All available units — when provided, each active family expands its unit chips */
  availableUnits?: Unit[];
  /** Currently selected unit code */
  selectedUnit?: string;
  /** Called when user picks a unit from the grouped list */
  onUnitSelect?: (code: string) => void;
  showCheck?: boolean;
  renderCheck?: (className?: string) => React.ReactNode;
};

type ProductMinimumUnitFieldProps = Pick<
  ProductUnitsSectionProps,
  'formData' | 'baseUnitOptions' | 'handleBaseUnitChange'
> & {
  onBlur?: () => void;
  showCheck?: boolean;
  renderCheck?: (className?: string) => React.ReactNode;
};

export const ProductUnitFamilyField: React.FC<ProductUnitFamilyFieldProps> = ({
  selectedUnitFamily,
  isUsingFallbackUnits,
  handleUnitFamilyChange,
  unitFamilies,
  availableUnits,
  selectedUnit,
  onUnitSelect,
  showCheck,
  renderCheck
}) => {
  // Group units by family when data is provided
  const unitsByFamily = React.useMemo((): Record<string, Unit[]> => {
    if (!availableUnits?.length) return {};
    return availableUnits.reduce<Record<string, Unit[]>>((acc, unit) => {
      const key = unit.category;
      if (!acc[key]) acc[key] = [];
      acc[key].push(unit);
      return acc;
    }, {});
  }, [availableUnits]);

  // Sort pills alphabetically by Spanish label
  const sortedFamilies = React.useMemo(
    () =>
      [...unitFamilies].sort((a, b) => {
        const la = UNIT_FAMILY_META[a]?.label ?? a;
        const lb = UNIT_FAMILY_META[b]?.label ?? b;
        return la.localeCompare(lb, 'es');
      }),
    [unitFamilies]
  );

  const activeUnits = unitsByFamily[selectedUnitFamily] ?? [];

  return (
    <div className="mb-5">
      <div className="flex items-center justify-between mb-2">
        <label className="flex items-center gap-2 text-ui-sm font-medium text-tertiary">
          <span>Familia de unidades</span>
          <Tooltip text="Selecciona la familia para habilitar conversiones (presentaciones)." position="right">
            <button
              type="button"
              aria-label="Ayuda: Familia de unidades"
              className="inline-flex items-center justify-center rounded-sm text-gray-400 hover:text-gray-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500/40"
            >
              <HelpCircle className="w-4 h-4" />
            </button>
          </Tooltip>
        </label>
        {showCheck && renderCheck?.()}
      </div>

      {/* Family pills — sorted A–Z by Spanish label */}
      <div className="flex flex-wrap gap-2" role="group" aria-label="Familia de unidades">
        {sortedFamilies.map(family => {
          const isActive = selectedUnitFamily === family;
          const meta = UNIT_FAMILY_META[family] ?? { label: family, Icon: Layers };
          const Icon = meta.Icon;
          const count = unitsByFamily[family]?.length ?? 0;
          return (
            <Chip
              key={family}
              size="sm"
              selected={isActive}
              onClick={() => handleUnitFamilyChange(family)}
              icon={<Icon />}
              badge={count > 0 ? count : undefined}
              aria-pressed={isActive}
            >
              {meta.label}
            </Chip>
          );
        })}
      </div>

      {/* Grouped unit chips for active family */}
      {availableUnits && activeUnits.length > 0 && onUnitSelect && (
        <div className="mt-4 flex flex-wrap gap-1" role="listbox" aria-label="Unidades disponibles">
          {activeUnits.map(unit => {
            const isSelected = unit.code === selectedUnit;
            const label = resolveUnitLabelText(unit);
            return (
              <button
                key={unit.code}
                type="button"
                role="option"
                aria-selected={isSelected}
                onClick={() => onUnitSelect(unit.code)}
                className={`rounded-md border px-2.5 py-1 text-[11px] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-500/30 ${
                  isSelected
                    ? 'border-violet-500 bg-violet-100 text-violet-900 font-semibold shadow-sm'
                    : 'border-gray-200 bg-white text-gray-600 hover:border-violet-300 hover:bg-violet-50 hover:text-violet-800'
                }`}
              >
                {label}
              </button>
            );
          })}
        </div>
      )}

      {availableUnits && activeUnits.length === 0 && (
        <p className="text-[11px] text-amber-600 mt-1">
          No hay unidades configuradas para esta familia.
        </p>
      )}

      {isUsingFallbackUnits && (
        <p className="text-[11px] text-amber-600 mt-1">
          Esta familia no tiene unidades configuradas. Selecciona otra familia o configura unidades en Configuración de Negocio.
        </p>
      )}
    </div>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const resolveUnitLabelText = (unit: Unit): string => {
  if (unit.description) {
    return unit.description;
  }
  return `(${unit.code}) ${unit.name}`;
};

export const ProductMinimumUnitField: React.FC<ProductMinimumUnitFieldProps> = ({
  formData,
  baseUnitOptions,
  handleBaseUnitChange,
  onBlur,
  showCheck,
  renderCheck
}) => {
  const hasUnits = baseUnitOptions.length > 0;
  return (
    <Select
      id="unidad"
      label="Unidad mínima"
      required
      labelAction={
        <Tooltip text="Unidad base para stock y ventas (ej.: Unidad, Kg)." position="right">
          <button
            type="button"
            aria-label="Ayuda: Unidad mínima"
            className="inline-flex items-center justify-center rounded-sm text-gray-400 hover:text-gray-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500/40"
          >
            <HelpCircle className="w-4 h-4" />
          </button>
        </Tooltip>
      }
      value={formData.unidad}
      onChange={(e) => handleBaseUnitChange(e.target.value as ProductFormData['unidad'])}
      onBlur={onBlur}
      disabled={!hasUnits}
      rightIcon={showCheck ? renderCheck?.() : undefined}
      size="small"
    >
      {hasUnits ? (
        baseUnitOptions.map(unit => (
          <option key={unit.id} value={unit.code}>
            {resolveUnitLabelText(unit)}
          </option>
        ))
      ) : (
        <option value="">Sin unidades configuradas</option>
      )}
    </Select>
  );
};

export const ProductUnitsSection: React.FC<ProductUnitsSectionProps> = ({
  formData,
  selectedUnitFamily,
  unitFamilies,
  baseUnitOptions,
  isUsingFallbackUnits,
  handleUnitFamilyChange,
  handleBaseUnitChange
}) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
      <div className="md:col-span-8">
        <ProductUnitFamilyField
          selectedUnitFamily={selectedUnitFamily}
          isUsingFallbackUnits={isUsingFallbackUnits}
          handleUnitFamilyChange={handleUnitFamilyChange}
          unitFamilies={unitFamilies}
        />
      </div>

      <div className="md:col-span-4">
        <ProductMinimumUnitField
          formData={formData}
          baseUnitOptions={baseUnitOptions}
          handleBaseUnitChange={handleBaseUnitChange}
        />
        {baseUnitOptions.length === 0 && (
          <p className="text-[11px] text-amber-600 mt-2">
            Configura tus unidades de medida en Configuración de Negocio.
          </p>
        )}
      </div>
    </div>
  );
};


